import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GlobalStyles } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getMuiTheme } from "config/theme";
import { muiLocales } from "i18n";

const STORAGE_KEY = "healthdesk.colorMode";

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch (e) {
    // Storage blocked (private window, etc.) — fall through to the system setting.
  }
  return null;
};

const ColorModeContext = createContext({
  mode: "light",
  preference: "system",
  setPreference: () => {},
});

const systemMode = () =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";

/**
 * Light/dark preference for the app: "system" (follow the OS, the default), "light"
 * or "dark", picked in Settings and remembered on this device. Only an explicit
 * light/dark pick is stored; choosing System clears it.
 */
export const ColorModeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(
    () => readStoredMode() || "system"
  );
  const [system, setSystem] = useState(systemMode);

  // Follow the OS live while on System, e.g. when it switches at sunset.
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return undefined;
    const onChange = () => setSystem(query.matches ? "dark" : "light");
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  const setPreference = useCallback((next) => {
    setPreferenceState(next);
    try {
      if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // Not persisted; the choice still applies for this session.
    }
  }, []);

  const mode = preference === "system" ? system : preference;

  return (
    <ColorModeContext.Provider value={{ mode, preference, setPreference }}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);

// src/index.css hard-codes light colors for the page, links, nav item titles and the
// chatscope kit. Re-point them at the theme in dark mode; light mode keeps index.css as-is.
// The body rules matter on public pages, which have no CssBaseline of their own.
const darkGlobalStyles = (theme) => ({
  html: { colorScheme: "dark" },
  body: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  a: { color: theme.palette.primary.main },
  "a .MuiListItemText-primary": { color: theme.palette.text.primary },
  ".cs-message-input__content-editor-wrapper, .cs-message-input__content-editor, .cs-message--incoming .cs-message__content":
    {
      backgroundColor: `${theme.palette.action.hover} !important`,
      color: `${theme.palette.text.primary} !important`,
    },
  ".cs-main-container, .cs-chat-container, .cs-message-list, .cs-message-input, .cs-conversation-header, .cs-typing-indicator":
    {
      backgroundColor: `${theme.palette.background.default} !important`,
      color: theme.palette.text.primary,
      borderColor: `${theme.palette.divider} !important`,
    },
});

/**
 * Applies the current color mode to everything below it. Wraps the dashboard shell and
 * the MUI-built public pages (auth, onboarding, VisitStart, CareStart, share, ...).
 * The legacy styled-components pages (Kijabe embeds, /chat, legal docs) stay outside
 * it: they paint their own light colors and would come out half dark.
 */
export const ColorModeThemeProvider = ({ children }) => {
  const { mode } = useColorMode();
  // The language rides on the theme too: MUI's own strings (pagination, "No options").
  const { i18n } = useTranslation();
  const theme = useMemo(() => {
    const base = getMuiTheme(mode);
    const locales = muiLocales(i18n.language);
    return locales.length ? createTheme(base, ...locales) : base;
  }, [mode, i18n.language]);
  return (
    <ThemeProvider theme={theme}>
      {/* ⚠️ MOUNT, DON'T EMPTY. A GlobalStyles that renders no rules keeps a stale pointer
          to a sibling <style> tag; once CssBaseline swaps its tag on a theme change, the next
          insert throws "insertBefore ... not a child of this node" and takes the app down. */}
      {mode === "dark" && <GlobalStyles styles={darkGlobalStyles} />}
      {children}
    </ThemeProvider>
  );
};
